import { KisweTab, KisweTheme, TabPushMode, TabViewMode } from '@/modules/kisweui/types'
import store from '@/store'
import { computed, Ref } from '@vue/composition-api'
import useCurrentCast from './currentCast'
import { TalkbackTargetType } from '@/types/streams'
import { ConnectionTabId } from '../types'
import useIsCastType from './isCastType'
import { Modals } from '@/modules/ui/types'

/*
  The intent here is to centralize all possible "connection tabs". The current communication between <ConnectionTabs>
  and <KisweTabContainer> is seriously lacking. KisweTabContainer was initially built to do one simple thing:
  clicking on a button opens a panel.

  This works great for the tabs in the ActionPalette, but for ConnectionTabs we have two different functions.
  1. For chat and connect this works the same way: opening and closing a PANEL.
  2. For most other "tabs" (which aren't actually tabs by definition), eg self-mute and talkback they represent a STATE.
  For example, they show either "enabled" or "disabled", but just execute something in the store and represent the
  store value that is set based on that state.

  The downside is that <KisweTabContainer> does not differentiate at this point, in the eyes of that component, they're
  still tabs that open a PANEL. So I started the groundwork of some refactoring that can be done in the future:
  differentiate between PANEL and STATE buttons.
*/

const useConnectionTabs = (withConnect: Ref<boolean>) => {

  const { currentCast } = useCurrentCast()
  const { isPlainCast } = useIsCastType(currentCast)

  const isStreamingSetupVisible = computed({
    get () { return store.getters.ui.isModalOpen(Modals.USER_MEDIA_CONNECT) },
    async set (state: boolean) {
      if (state) await store.dispatch.ui.openModal(Modals.USER_MEDIA_CONNECT)
      else await store.dispatch.ui.closeModal(Modals.USER_MEDIA_CONNECT)
    }
  })
  const isConnected = computed(() => store.state.usermedia.isConnected)
  const isCaster = computed(() => store.getters.events.currentUserIsACaster)
  const isSelfMuted = computed(() => store.state.usermedia.isSelfMuted)
  const isScreenSharing = computed(() => store.getters.streams.isScreensharing)
  const talkbackTarget = computed(() => store.state.talkback.target)
  const totalNewMessages = computed(() => {
    if (!currentCast.value?.chat_id) return 0
    return store.getters.chat.totalNewMessages(currentCast.value.chat_id)
  })

  // eslint-disable-next-line complexity
  const allConnectionTabs = computed<(KisweTab & { isVisible: boolean })[]>(() => [{
    id: ConnectionTabId.Chat,
    icon: 'chat',
    type: 'text',
    notification: totalNewMessages.value > 0,
    viewMode: TabViewMode.Panel,
    tooltip: { content: 'Chat', trigger: 'hover' },
    isVisible: true
  }, {
    id: ConnectionTabId.ScreenShareStart,
    icon: 'fa-desktop',
    type: 'text',
    theme: KisweTheme.Secondary,
    isVisible: isPlainCast.value && isCaster.value && !isScreenSharing.value
  }, {
    id: ConnectionTabId.ScreenShareStop,
    icon: 'fa-desktop',
    type: 'text',
    theme: KisweTheme.Danger,
    isVisible: isPlainCast.value && isCaster.value && isScreenSharing.value
  }, {
    id: ConnectionTabId.SelfMuteInactive,
    icon: 'microphone-slash',
    type: 'text',
    viewMode: TabViewMode.State,
    group: 'self-mute',
    tooltip: { content: 'Mute', trigger: 'hover' },
    theme: KisweTheme.Secondary,
    isVisible: isCaster.value && !isSelfMuted.value,
    disabled: !isConnected.value
  }, {
    id: ConnectionTabId.SelfMuteActive,
    icon: 'microphone-slash',
    type: 'text',
    viewMode: TabViewMode.State,
    group: 'self-mute',
    tooltip: { content: 'Unmute', trigger: 'hover' },
    theme: KisweTheme.Primary,
    isVisible: isCaster.value && isSelfMuted.value
  }, {
    id: ConnectionTabId.Connect,
    icon: isCaster.value ? 'video' : 'headphone-mic',
    type: 'text',
    tooltip: {
      content: isCaster.value ? 'Start streaming' : 'Activate talkback',
      trigger: 'hover'
    },
    viewMode: TabViewMode.Panel,
    group: 'talkback-connect',
    selected: isStreamingSetupVisible.value,
    isVisible: !isConnected.value
  }, {
    id: ConnectionTabId.TalkbackAll,
    icon: 'all',
    type: 'text',
    pushMode: TabPushMode.Hold,
    viewMode: TabViewMode.State,
    group: `talkback-${ TalkbackTargetType.ALL }`,
    tooltip: { content: 'Intercom', trigger: 'hover' },
    selected: talkbackTarget.value === TalkbackTargetType.ALL,
    isVisible: !isCaster.value && isConnected.value
  }, {
    id: ConnectionTabId.TalkbackCrew,
    icon: 'headphone-mic',
    type: 'text',
    pushMode: TabPushMode.Toggle,
    viewMode: TabViewMode.State,
    group: `talkback-${ TalkbackTargetType.CREW }`,
    tooltip: { content: 'Talkback', trigger: 'hover' },
    selected: talkbackTarget.value === TalkbackTargetType.CREW,
    isVisible: !isCaster.value && isConnected.value
  }, {
    id: ConnectionTabId.Disconnect,
    icon: 'stop',
    type: 'icon',
    viewMode: TabViewMode.State,
    group: 'talkback-connect',
    theme: KisweTheme.Danger,
    tooltip: {
      content: isCaster.value ? 'Stop streaming' : 'Stop talkback',
      trigger: 'hover'
    },
    isVisible: isConnected.value
  }])

  const visibleConnectionTabs = computed<KisweTab[]>(() => {
    if (!withConnect.value) return [allConnectionTabs.value[0]]
    return allConnectionTabs.value.filter((tab) => tab.isVisible)
  })

  return {
    isCaster,
    isConnected,
    isStreamingSetupVisible,
    visibleConnectionTabs
  }
}

export default useConnectionTabs
